"use client";

import { useEffect, useRef } from "react";
import { useRouter } from "next/navigation";
import { useToast } from "@test-prep/ui";

type FlashToastProps = {
  type: "success" | "error";
  message: string;
  /**
   * URL (path + optional query string, no flash param) to replace the
   * current entry with once the toast has fired, so a page refresh doesn't
   * re-show it.
   */
  stripToPath: string;
};

/**
 * Fires a toast once on mount for results that only exist because the
 * server action `redirect()`ed here (createTenantAction, deleteTenantAction,
 * changeOwnPasswordAction) — a toast dispatched before the redirect would be
 * torn down by the navigation, so the destination page renders this
 * component instead, keyed off a one-time flash query param computed
 * server-side (e.g. `?created=1`).
 *
 * `firedRef` is a ref, not state, so this never calls a `useState` setter
 * inside the effect — see use-action-toast.ts for why that matters here.
 */
export function FlashToast({ type, message, stripToPath }: FlashToastProps) {
  const toast = useToast();
  const router = useRouter();
  const firedRef = useRef(false);

  useEffect(() => {
    if (firedRef.current) {
      return;
    }
    firedRef.current = true;
    toast.show({ type, msg: message });
    router.replace(stripToPath);
  }, [type, message, stripToPath, toast, router]);

  return null;
}
